import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/modal';
import {
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
} from '@chakra-ui/react';
import InputField from 'components/fields/InputField';
import { useEffect, useState } from 'react';
import { createProduction, getSuggestion } from 'interfaces/productions';
import { MdWarning, MdCheckCircle, MdInventory, MdPlayArrow, MdSchedule, MdShoppingCart, MdFactory } from 'react-icons/md';

const ProductionPreviewModal = (props: {
    isOpen: any;
    onClose: any;
    onProduction: any;
    product: any;
    productName: string;
}) => {
    const { isOpen, onClose, product, productName, onProduction } = props;
    const [suggestions, setSuggestions] = useState<any>([]);

    const getFifoSuggestions = async () => {
        const res = await getSuggestion({
            product: product.product,
            quantity: product.quantity,
        });
        if (!res) return;
        // Adding production form data
        res.date = product.date;
        res.comment = product.comment;
        res.area = product.area;
        res.lotNumber = product.lotNumber;
        setSuggestions(res);
    };

    const handleChange = (ingId: any, moveId: any, value: any) => {
        const newSuggestions = Object.assign({}, suggestions);
        const ingIndex = newSuggestions.ingredients.findIndex(
            (ing: any) => ing.id === ingId,
        );
        const movIndex = newSuggestions.ingredients[
            ingIndex
        ].movements.findIndex((mov: any) => mov.id === moveId);
        if (value === '')
            newSuggestions.ingredients[ingIndex].movements[movIndex].quantity =
                '';
        else
            newSuggestions.ingredients[ingIndex].movements[movIndex].quantity =
                Number(value);
        setSuggestions(newSuggestions);
        console.log(suggestions);
    };

    useEffect(() => {
        if (product && product.product && product.product !== '' && isOpen) {
            getFifoSuggestions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product, isOpen]);

    const startProduction = async () => {
        const res = await createProduction(suggestions);
        if (res.error) return;
        onProduction(res.data);
    };

    const isProductionPossible = () => {
        if (suggestions && suggestions.ingredients) {
            for (let i = 0; i < suggestions.ingredients.length; i++) {
                const ing = suggestions.ingredients[i];
                if (ing.quantity < ing.totalNeeded) return false;
            }
            return true;
        }
        return false;
    };

    const getSuggest = () => {
        if (suggestions && suggestions.ingredients)
            return suggestions.ingredients;
        return [];
    };

    const isProdToday = () => {
        const today = new Date();
        const prodDate = new Date(product.date);
        return (
            today.getDate() === prodDate.getDate() &&
            today.getMonth() === prodDate.getMonth() &&
            today.getFullYear() === prodDate.getFullYear()
        );
    };

    const getMissingProductsUrl = () => {
        const missingProducts = getSuggest().filter(
            (ing: any) => ing.quantity < ing.totalNeeded,
        );
        if (missingProducts.length === 0) return '';
        let url = 'missingP=';
        missingProducts.forEach((ing: any) => {
            url += `${ing.name},`;
        });
        return url.slice(0, -1);
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay className="bg-[#5a5c5a]/70" />
                <ModalContent className="!m-auto !w-max min-w-[350px] !max-w-[750px] !max-h-[80vh]">
                    <ModalBody className="!p-0 !max-h-[80vh] overflow-hidden">
                        <div className="w-full h-full flex flex-col max-h-[80vh] overflow-hidden bg-white dark:bg-navy-800 rounded-2xl">
                            <div className="px-5 pt-4 pb-2 flex-shrink-0">
                                <h4 className="text-base font-bold text-navy-700 dark:text-white">
                                    Prévisualisation
                                </h4>
                            </div>
                            {/* Product card */}
                            <div className="mx-5 mb-3 rounded-xl bg-gray-50 p-3 dark:bg-navy-700 flex-shrink-0">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white dark:bg-navy-700">
                                            <MdInventory className="h-5 w-5 text-toola-500" />
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Produit</p>
                                            <p className="text-base font-bold text-navy-700 dark:text-white">
                                                {productName}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Quantité</p>
                                        <div className="flex items-center gap-1">
                                            <p className="text-xl font-bold text-green-500">
                                                +{product.quantity}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Ingredients section */}
                            <div className="flex-1 flex flex-col overflow-hidden min-h-0 px-5">
                                <div className="mb-2 flex items-center justify-between flex-shrink-0">
                                    <h3 className="text-base font-bold text-navy-700 dark:text-white">
                                        Ingrédients nécessaires
                                    </h3>
                                    {getSuggest().length > 0 && (
                                        <div className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
                                            isProductionPossible() 
                                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                        }`}>
                                            {isProductionPossible() ? (
                                                <>
                                                    <MdCheckCircle className="h-4 w-4" />
                                                    <span>Stock OK</span>
                                                </>
                                            ) : (
                                                <>
                                                    <MdWarning className="h-4 w-4" />
                                                    <span>Stock insuffisant</span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                                
                                {getSuggest().length === 0 ? (
                                    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50/50 py-6 text-center text-gray-500 dark:border-navy-600 dark:bg-navy-800/50 flex-shrink-0">
                                        <MdInventory className="h-10 w-10 text-gray-400" />
                                        <p className="mt-2 text-sm">Aucun ingrédient nécessaire pour cette production.</p>
                                    </div>
                                ) : (
                                    <div className="flex-1 overflow-y-auto pr-1 min-h-0" style={{ scrollbarWidth: 'thin' }}>
                                        <Accordion allowMultiple defaultIndex={[0]} className="flex flex-col gap-2 pb-2">
                                            {getSuggest().map((ing: any, index: number) => {
                                                const sum = ing.movements.reduce(
                                                    (a: any, b: any) => a + (b.quantity || 0),
                                                    0,
                                                );
                                                const isMissing = ing.quantity < ing.totalNeeded;
                                                
                                                return (
                                                    <AccordionItem key={index} className="border-b-0 rounded-xl border border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-800 overflow-hidden">
                                                        <h2>
                                                            <AccordionButton className="flex justify-between bg-gray-50 p-3 hover:bg-gray-100 dark:bg-navy-700 dark:hover:bg-navy-600">
                                                                <div className="flex items-center gap-2 text-left flex-1">
                                                                    <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                                                                        isMissing 
                                                                            ? 'bg-red-100 dark:bg-red-900/30' 
                                                                            : 'bg-green-100 dark:bg-green-900/30'
                                                                    }`}>
                                                                        {isMissing ? (
                                                                            <MdWarning className="h-4 w-4 text-red-600 dark:text-red-400" />
                                                                        ) : (
                                                                            <MdCheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                                                                        )}
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <span className="font-bold text-navy-900 dark:text-white block text-sm">
                                                                            {ing.name}
                                                                        </span>
                                                                        <div className="flex items-center gap-2 mt-0.5">
                                                                            <span className={`text-xs font-semibold ${isMissing ? 'text-red-500' : 'text-gray-500'}`}>
                                                                                Stock: {ing.quantity.toFixed(2)} {ing.unity}
                                                                            </span>
                                                                            {isMissing && (
                                                                                <span className="text-xs font-bold text-red-500">
                                                                                    • Requis: {ing.totalNeeded.toFixed(2)}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {ing.expiredStockQuantity > 0 && (
                                                                            <div className="flex items-center gap-1 mt-0.5">
                                                                                <MdWarning className="h-3 w-3 text-orange-500" />
                                                                                <span className="text-xs font-bold text-orange-500">
                                                                                    {ing.expiredStockQuantity.toFixed(2)} {ing.unity} périmés
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <div className="text-right">
                                                                        <span className="text-xs text-gray-500 block">À utiliser</span>
                                                                        <span className="font-bold text-red-500 dark:text-red-400">
                                                                            - {sum} {ing.unity}
                                                                        </span>
                                                                    </div>
                                                                    <AccordionIcon className="text-navy-700 dark:text-white" />
                                                                </div>
                                                            </AccordionButton>
                                                        </h2>
                                                        <AccordionPanel pb={3} className="bg-white p-3 dark:bg-navy-800">
                                                            <div className="flex flex-col gap-2">
                                                                <div className="grid grid-cols-12 gap-3 text-xs font-medium text-gray-500 uppercase px-1">
                                                                    <div className="col-span-7">Source (Zone, DLC, Lot)</div>
                                                                    <div className="col-span-5 text-right">Quantité à utiliser</div>
                                                                </div>
                                                                {ing.movements.map((mov: any, idx: number) => (
                                                                    <div key={idx} className="grid grid-cols-12 gap-3 items-center bg-gray-50 dark:bg-navy-900/50 p-2.5 rounded-lg">
                                                                        <div className="col-span-7 text-sm text-gray-700 dark:text-gray-300">
                                                                            <div className="font-bold text-navy-700 dark:text-white text-xs">
                                                                                {mov.area} • {mov.lotNumber || 'Sans lot'}
                                                                            </div>
                                                                            <div className="text-xs text-gray-500 mt-0.5">
                                                                                DLC: {mov.dlc ? mov.dlc.split('T')[0] : 'N/A'} • Dispo: <span className="font-bold">{mov.stockQuantity.toFixed(2)}</span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-span-5">
                                                                            <InputField
                                                                                variant="auth"
                                                                                state={mov.quantity > mov.stockQuantity ? 'error' : ''}
                                                                                placeholder={mov.quantity.toString()}
                                                                                id={`quantity-${ing.id}-${mov.id}`}
                                                                                type="number"
                                                                                min="0"
                                                                                extra="text-right font-bold w-full text-sm"
                                                                                onChange={(e: any) => handleChange(ing.id, mov.id, e.target.value)}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </AccordionPanel>
                                                    </AccordionItem>
                                                );
                                            })}
                                        </Accordion>
                                    </div>
                                )}
                            </div>

                            {/* Boutons d'action */}
                            <div className="px-5 py-3 flex flex-col gap-2 flex-shrink-0 border-t border-gray-200 dark:border-navy-700 bg-gray-50 dark:bg-navy-900">
                                <div className="flex gap-2">
                                    <button
                                        onClick={onClose}
                                        className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700">
                                    
                                        Retour
                                    </button>
                                    
                                    {isProductionPossible() && isProdToday() && (
                                        <button
                                            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-toola-500 px-3 py-2 text-sm font-medium text-white hover:bg-toola-600 dark:bg-toola-600 dark:hover:bg-toola-700"
                                            onClick={() => startProduction()}
                                        >
                                            <MdPlayArrow className="h-4 w-4" />
                                            Lancer la production
                                        </button>
                                    )}
                                    
                                    {!isProdToday() && (
                                        <button
                                            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-orange-500 px-3 py-2 text-sm font-medium text-white hover:bg-orange-600"
                                            onClick={() => startProduction()}
                                        >
                                            <MdSchedule className="h-4 w-4" />
                                            Programmer la production
                                        </button>
                                    )}
                                </div>
                                
                                {!isProductionPossible() && isProdToday() && (
                                    <div className="flex gap-2">
                                        <button
                                            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-orange-500 px-3 py-2 text-xs font-medium text-white hover:bg-orange-600"
                                            onClick={() =>
                                                window.open(
                                                    `/stock/movements?entry=true&${getMissingProductsUrl()}`,
                                                    '_blank',
                                                )
                                            }
                                        >
                                            <MdShoppingCart className="h-4 w-4" />
                                            Rajouter du stock
                                        </button>
                                        <button
                                            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-toola-500 px-3 py-2 text-xs font-medium text-white hover:bg-toola-600"
                                            onClick={() =>
                                                window.open(
                                                    `/stock/production`,
                                                    '_blank',
                                                )
                                            }
                                        >
                                            <MdFactory className="h-4 w-4" />
                                            Autre production
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

export default ProductionPreviewModal;
